import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Search, CreditCard as Edit3, Save, X as XIcon, Trash2, Link2, AlertTriangle, Check, Wand2, ChevronDown } from 'lucide-react';
import { useFontAliasStore, type FontAlias } from '../../stores/fontAliasStore';
import { useFontStore } from '../../stores/fontStore';
import { useStudioStore } from '../../stores/studioStore';
import { loadCustomFont, type CustomFont } from '../../lib/fontLoader';
import type { CanvasElement, TextElement, ButtonElement } from '../../types';

const PREVIEW_SAMPLE = 'AaBbCc 123';

function weightLabel(weight: number): string {
  const map: Record<number, string> = {
    100: 'Thin',
    200: 'ExtraLight',
    300: 'Light',
    400: 'Regular',
    500: 'Medium',
    600: 'SemiBold',
    700: 'Bold',
    800: 'ExtraBold',
    900: 'Black',
  };
  return map[weight] || `Weight ${weight}`;
}

function variantDisplayName(weight: number, style: string): string {
  const base = weightLabel(weight);
  return style === 'italic' ? `${base} Italic` : base;
}

interface FontMappingsTabProps {
  onJumpToCss?: (cssValue: string) => void;
}

export function FontMappingsTab({ onJumpToCss: _onJumpToCss }: FontMappingsTabProps) {
  const { aliases, isLoading, isLoaded, ensureLoaded, refresh, upsertAlias, deleteAlias } = useFontAliasStore();
  const fonts = useFontStore((s) => s.fonts);
  const canvasElements = useStudioStore((s) => s.canvasState.elements);
  const updateElements = useStudioStore((s) => s.updateElements);

  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<FontAlias | null>(null);

  useEffect(() => {
    ensureLoaded().catch(() => {});
  }, [ensureLoaded]);

  const familyToVariants = useMemo(() => {
    const map = new Map<string, CustomFont[]>();
    for (const f of fonts) {
      if (!f.isActive) continue;
      const list = map.get(f.fontFamily) || [];
      list.push(f);
      map.set(f.fontFamily, list);
    }
    for (const [k, v] of map.entries()) {
      map.set(
        k,
        [...v].sort((a, b) => {
          const aw = typeof a.fontWeight === 'number' ? a.fontWeight : parseInt(String(a.fontWeight), 10) || 400;
          const bw = typeof b.fontWeight === 'number' ? b.fontWeight : parseInt(String(b.fontWeight), 10) || 400;
          return aw - bw;
        })
      );
    }
    return map;
  }, [fonts]);

  const availableFamilies = useMemo(() => Array.from(familyToVariants.keys()).sort(), [familyToVariants]);

  const filteredAliases = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return aliases;
    return aliases.filter(
      (a) =>
        a.originalCss.toLowerCase().includes(q) ||
        a.assignedFamily.toLowerCase().includes(q)
    );
  }, [aliases, query]);

  const countElementsForAlias = (alias: FontAlias) => {
    let bySource = 0;
    let byFamily = 0;
    for (const el of canvasElements) {
      if (el.type !== 'text' && el.type !== 'button') continue;
      const te = el as TextElement | ButtonElement;
      if (te.svgFontSource && te.svgFontSource === alias.originalCss) bySource++;
      else if ((te.fontFamily || '').trim() === alias.assignedFamily) byFamily++;
    }
    return { bySource, byFamily };
  };

  const applyMappingToPlacedText = async (
    alias: FontAlias,
    previousAssignedFamily: string
  ) => {
    const updates: Array<{ id: string; changes: Partial<CanvasElement> }> = [];
    for (const el of canvasElements) {
      if (el.type !== 'text' && el.type !== 'button') continue;
      const te = el as TextElement | ButtonElement;
      const currentFamily = (te.fontFamily || '').trim();
      const matchesBySource = !!te.svgFontSource && te.svgFontSource === alias.originalCss;
      const matchesByFamily = !te.svgFontSource && currentFamily === previousAssignedFamily;
      if (!matchesBySource && !matchesByFamily) continue;

      const changes: Partial<CanvasElement> = {
        fontFamily: alias.assignedFamily,
        svgFontSource: alias.originalCss,
      } as Partial<CanvasElement>;
      if (el.type === 'text') {
        (changes as Partial<TextElement>).fontWeight = alias.assignedWeight ?? (te as TextElement).fontWeight ?? 400;
        (changes as Partial<TextElement>).fontStyle =
          (alias.assignedStyle === 'italic' ? 'italic' : 'normal');
      } else {
        (changes as Partial<ButtonElement>).fontWeight = alias.assignedWeight ?? (te as ButtonElement).fontWeight ?? 400;
      }
      updates.push({ id: te.id, changes });
    }
    if (updates.length > 0) {
      updateElements(updates);
    }
    return updates.length;
  };

  return (
    <div className="p-6">
      <div className="mb-4 px-3 py-2 bg-white/[0.03] border border-white/[0.06] rounded-lg text-[11px] text-white/50 leading-relaxed">
        <span className="text-white/70 font-semibold">SVG Mappings</span>
        {' — '}
        when an SVG import references a font by its CSS name, this table binds
        that name to one of your uploaded project fonts. To see the fonts
        themselves with previews, switch to the{' '}
        <span className="text-white/70 font-semibold">Families</span> tab.
      </div>
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search original CSS or target family..."
            className="w-full pl-9 pr-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-cyan-500/50"
          />
        </div>
        <button
          onClick={() => refresh()}
          disabled={isLoading}
          className="text-xs text-white/50 hover:text-white/80 transition-colors"
        >
          {isLoading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {!isLoaded && isLoading ? (
        <div className="py-12 text-center text-white/40 text-sm flex items-center justify-center gap-2">
          <Loader2 size={14} className="animate-spin" />
          Loading mappings...
        </div>
      ) : aliases.length === 0 ? (
        <div className="py-12 text-center">
          <Link2 size={36} className="mx-auto text-white/20 mb-3" />
          <p className="text-sm text-white/50 mb-1">No font mappings yet</p>
          <p className="text-xs text-white/30">
            Mappings are created automatically when you import an SVG with custom fonts.
          </p>
        </div>
      ) : filteredAliases.length === 0 ? (
        <div className="py-8 text-center text-white/40 text-sm">
          No mappings match "{query}"
        </div>
      ) : (
        <div className="space-y-2">
          {filteredAliases.map((alias) => {
            const isEditing = editing === alias.id;
            const counts = countElementsForAlias(alias);
            const affectable = counts.bySource + counts.byFamily;
            const assignedExists = availableFamilies.includes(alias.assignedFamily);
            return (
              <MappingRow
                key={alias.id}
                alias={alias}
                isEditing={isEditing}
                availableFamilies={availableFamilies}
                familyToVariants={familyToVariants}
                assignedExists={assignedExists}
                affectableCount={affectable}
                onEdit={() => setEditing(alias.id)}
                onCancelEdit={() => setEditing(null)}
                onSave={async (payload, applyToPlaced) => {
                  const previousAssignedFamily = alias.assignedFamily;
                  const result = await upsertAlias(payload);
                  if (!result.ok) {
                    alert(`Failed to save mapping: ${result.error ?? 'unknown error'}`);
                    return;
                  }
                  setEditing(null);
                  if (applyToPlaced) {
                    await applyMappingToPlacedText(
                      { ...alias, ...payload, assignedStyle: payload.assignedStyle } as FontAlias,
                      previousAssignedFamily
                    );
                  }
                }}
                onDelete={() => setConfirmDelete(alias)}
              />
            );
          })}
        </div>
      )}

      {confirmDelete && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-xl z-20">
          <div className="bg-[#222] border border-white/10 rounded-lg p-6 max-w-sm mx-4 shadow-xl">
            <h3 className="text-white font-medium mb-2">Delete Mapping?</h3>
            <p className="text-white/50 text-sm mb-4">
              Remove the mapping for <span className="text-white/80 font-mono">"{confirmDelete.originalCss}"</span>?
              Placed text already using the assigned family will not change.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-3 py-1.5 text-white/60 hover:text-white text-sm transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const res = await deleteAlias(confirmDelete.originalCss);
                  if (!res.ok) {
                    alert(`Failed to delete: ${res.error ?? 'unknown error'}`);
                  }
                  setConfirmDelete(null);
                }}
                className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 text-sm font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MappingRow({
  alias,
  isEditing,
  availableFamilies,
  familyToVariants,
  assignedExists,
  affectableCount,
  onEdit,
  onCancelEdit,
  onSave,
  onDelete,
}: {
  alias: FontAlias;
  isEditing: boolean;
  availableFamilies: string[];
  familyToVariants: Map<string, CustomFont[]>;
  assignedExists: boolean;
  affectableCount: number;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSave: (
    payload: {
      originalCss: string;
      assignedFamily: string;
      assignedWeight: number | null;
      assignedStyle: string | null;
    },
    applyToPlaced: boolean
  ) => Promise<void>;
  onDelete: () => void;
}) {
  const [family, setFamily] = useState(alias.assignedFamily);
  const [variantKey, setVariantKey] = useState(
    `${alias.assignedWeight ?? 400}-${alias.assignedStyle ?? 'normal'}`
  );

  useEffect(() => {
    if (isEditing) {
      setFamily(alias.assignedFamily);
      setVariantKey(`${alias.assignedWeight ?? 400}-${alias.assignedStyle ?? 'normal'}`);
    }
  }, [isEditing, alias.id, alias.assignedFamily, alias.assignedWeight, alias.assignedStyle]);

  const variants = familyToVariants.get(family) || [];

  const commitPayload = () => {
    const [wStr, s] = variantKey.split('-');
    const w = parseInt(wStr, 10);
    return {
      originalCss: alias.originalCss,
      assignedFamily: family,
      assignedWeight: Number.isFinite(w) ? w : null,
      assignedStyle: s || 'normal',
    };
  };

  return (
    <div className="border border-white/10 rounded-lg p-3">
      <div className="grid grid-cols-[1fr_auto_1fr_auto] gap-3 items-start">
        {/* LEFT COLUMN: source CSS */}
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-wider text-white/40">SVG Source</span>
            {!assignedExists && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded"
                title={`The target family "${alias.assignedFamily}" is not currently uploaded.`}
              >
                <AlertTriangle size={10} />
                Missing
              </span>
            )}
          </div>
          <div
            className="text-sm font-mono text-white/90 truncate"
            title={alias.originalCss}
          >
            {alias.originalCss}
          </div>
          {affectableCount > 0 && (
            <div className="mt-2 text-[11px] text-white/50">
              {affectableCount} placed text element{affectableCount === 1 ? '' : 's'} will accept this mapping.
            </div>
          )}
          {!isEditing && assignedExists && (
            <div className="mt-2 flex items-center gap-1 text-[10px] text-emerald-400/80">
              <Check size={10} />
              Target uploaded
            </div>
          )}
        </div>

        {/* arrow column */}
        <div className="pt-5 text-white/30 text-sm select-none">→</div>

        {/* RIGHT COLUMN: assigned family (family-first, variant secondary) */}
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">
            Project Font
          </div>
          {isEditing ? (
            <div className="space-y-2">
              <FamilyCombobox
                value={family}
                families={availableFamilies}
                onChange={(f) => {
                  setFamily(f);
                  const firstVariant = familyToVariants.get(f)?.[0];
                  if (firstVariant) {
                    const w =
                      typeof firstVariant.fontWeight === 'number'
                        ? firstVariant.fontWeight
                        : parseInt(String(firstVariant.fontWeight), 10) || 400;
                    setVariantKey(`${w}-${firstVariant.fontStyle || 'normal'}`);
                  }
                }}
              />
              <VariantCombobox
                family={family}
                variants={variants}
                value={variantKey}
                onChange={setVariantKey}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-0.5">
              <span
                className="text-sm text-white truncate"
                style={{ fontFamily: `"${alias.assignedFamily}", sans-serif` }}
                title={alias.assignedFamily}
              >
                {alias.assignedFamily}
              </span>
              <span
                className="text-[11px] text-white/60"
                style={{
                  fontFamily: `"${alias.assignedFamily}", sans-serif`,
                  fontWeight: alias.assignedWeight ?? 400,
                  fontStyle: alias.assignedStyle === 'italic' ? 'italic' : 'normal',
                }}
              >
                {variantDisplayName(alias.assignedWeight ?? 400, alias.assignedStyle ?? 'normal')}
                {' '}
                <span className="text-white/40">· {PREVIEW_SAMPLE}</span>
              </span>
            </div>
          )}
        </div>

        {/* actions column */}
        <div className="flex items-center gap-1 flex-shrink-0 pt-4">
          {isEditing ? (
            <>
              <button
                onClick={() => onCancelEdit()}
                className="p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors"
                title="Cancel"
              >
                <XIcon size={14} />
              </button>
              <button
                onClick={() => onSave(commitPayload(), false)}
                className="px-2 py-1 rounded bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 text-xs font-medium transition-colors flex items-center gap-1"
                title="Save mapping"
              >
                <Save size={12} />
                Save
              </button>
              <button
                onClick={() => onSave(commitPayload(), true)}
                disabled={affectableCount === 0}
                className="px-2 py-1 rounded bg-cyan-600 text-white hover:bg-cyan-500 text-xs font-medium transition-colors flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed"
                title={
                  affectableCount === 0
                    ? 'No placed text can be updated by this mapping'
                    : `Save and rewrite ${affectableCount} placed text element${affectableCount === 1 ? '' : 's'}`
                }
              >
                <Wand2 size={12} />
                Save &amp; Apply
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onEdit}
                className="p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors"
                title="Edit mapping"
              >
                <Edit3 size={14} />
              </button>
              <button
                onClick={onDelete}
                className="p-1.5 rounded hover:bg-red-500/20 text-white/40 hover:text-red-400 transition-colors"
                title="Delete mapping"
              >
                <Trash2 size={14} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FamilyCombobox({
  value,
  families,
  onChange,
}: {
  value: string;
  families: string[];
  onChange: (family: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      return;
    }
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    requestAnimationFrame(() => searchRef.current?.focus());
    return () => document.removeEventListener('mousedown', onClick);
  }, [isOpen]);

  // Ensure every family in the list actually has its @font-face registered so
  // the preview rows render in the real typeface instead of a fallback.
  useEffect(() => {
    if (!isOpen) return;
    for (const f of families) {
      loadCustomFont(f).catch(() => {});
    }
  }, [isOpen, families]);

  const filtered = search.trim()
    ? families.filter((f) => f.toLowerCase().includes(search.trim().toLowerCase()))
    : families;

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm text-white hover:border-white/20 focus:outline-none focus:border-cyan-500/50 transition-colors"
      >
        <span
          className="truncate"
          style={value ? { fontFamily: `"${value}", sans-serif` } : undefined}
        >
          {value || <span className="text-white/40">Select family</span>}
        </span>
        <ChevronDown size={12} className="text-white/40 flex-shrink-0" />
      </button>

      {isOpen && (
        <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-[#0d0d12] border border-white/10 rounded-lg shadow-2xl max-h-80 flex flex-col overflow-hidden">
          <div className="p-1.5 border-b border-white/[0.06]">
            <div className="relative">
              <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search families..."
                className="w-full pl-6 pr-2 py-1 text-xs bg-white/[0.04] border border-white/[0.06] rounded text-white placeholder-white/30 focus:outline-none focus:border-cyan-500/40"
              />
            </div>
          </div>
          <div className="overflow-y-auto sidebar-scroll flex-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-[11px] text-white/40">
                {families.length === 0
                  ? 'No active fonts uploaded.'
                  : `No families match "${search}".`}
              </div>
            ) : (
              filtered.map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => {
                    onChange(f);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-2.5 py-2 transition-colors flex flex-col gap-0.5 border-b border-white/[0.03] ${
                    value === f
                      ? 'bg-cyan-500/10'
                      : 'hover:bg-white/[0.04]'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className={`text-[11px] uppercase tracking-wider font-semibold ${
                        value === f ? 'text-cyan-400' : 'text-white/50'
                      }`}
                    >
                      {f}
                    </span>
                    {value === f && <Check size={12} className="flex-shrink-0 text-cyan-400" />}
                  </div>
                  <span
                    className="text-base text-white truncate"
                    style={{ fontFamily: `"${f}", sans-serif` }}
                  >
                    {PREVIEW_SAMPLE}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function VariantCombobox({
  family,
  variants,
  value,
  onChange,
}: {
  family: string;
  variants: CustomFont[];
  value: string;
  onChange: (variantKey: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [isOpen]);

  // Register every variant's @font-face so each preview row paints in its own weight/style.
  useEffect(() => {
    if (!isOpen) return;
    loadCustomFont(family).catch(() => {});
  }, [isOpen, family]);

  const parsed = variants.map((v) => {
    const w =
      typeof v.fontWeight === 'number'
        ? v.fontWeight
        : parseInt(String(v.fontWeight), 10) || 400;
    const style = v.fontStyle || 'normal';
    return { v, key: `${w}-${style}`, weight: w, style };
  });

  const selected = parsed.find((p) => p.key === value) || parsed[0];

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setIsOpen((x) => !x)}
        disabled={parsed.length === 0}
        className="w-full flex items-center justify-between gap-2 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm text-white hover:border-white/20 focus:outline-none focus:border-cyan-500/50 transition-colors disabled:opacity-40"
      >
        {selected ? (
          <span className="flex items-center gap-2 truncate min-w-0">
            <span className="text-[10px] uppercase tracking-wider text-white/50 flex-shrink-0">
              {variantDisplayName(selected.weight, selected.style)}
            </span>
            <span
              className="truncate text-sm"
              style={{
                fontFamily: `"${family}", sans-serif`,
                fontWeight: selected.weight,
                fontStyle: selected.style === 'italic' ? 'italic' : 'normal',
              }}
            >
              {PREVIEW_SAMPLE}
            </span>
          </span>
        ) : (
          <span className="text-white/40 text-xs">No variants</span>
        )}
        <ChevronDown size={12} className="text-white/40 flex-shrink-0" />
      </button>

      {isOpen && parsed.length > 0 && (
        <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-[#0d0d12] border border-white/10 rounded-lg shadow-2xl max-h-72 overflow-y-auto sidebar-scroll">
          {parsed.map(({ v, key, weight, style }) => (
            <button
              key={v.id}
              type="button"
              onClick={() => {
                onChange(key);
                setIsOpen(false);
              }}
              className={`w-full text-left px-2.5 py-2 transition-colors flex items-center justify-between gap-2 border-b border-white/[0.03] ${
                value === key
                  ? 'bg-cyan-500/10'
                  : 'hover:bg-white/[0.04]'
              }`}
            >
              <span className="flex items-center gap-2 min-w-0 flex-1">
                <span
                  className={`text-[10px] uppercase tracking-wider font-semibold w-20 flex-shrink-0 ${
                    value === key ? 'text-cyan-400' : 'text-white/50'
                  }`}
                >
                  {variantDisplayName(weight, style)}
                </span>
                <span
                  className="truncate text-base text-white"
                  style={{
                    fontFamily: `"${family}", sans-serif`,
                    fontWeight: weight,
                    fontStyle: style === 'italic' ? 'italic' : 'normal',
                  }}
                >
                  {PREVIEW_SAMPLE}
                </span>
              </span>
              {value === key && <Check size={12} className="flex-shrink-0 text-cyan-400" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
